import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "store";
import { globalSettingsChanged, layoutStateChanged, themeChanged } from "store/features";
import { GlobalSettings, LayoutState, PanelState } from "types";
import { parseFocusModeString } from "@sandbox/code-editor";

export const useUrlParameters = () => {

  const dispatch = useAppDispatch();
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const keyMap: { [key: string]: string } = {};

    urlParams.forEach((_value, key) => {
      keyMap[key.toLowerCase()] = key;
    });

    const resolve = (param: string, defaulState?: PanelState) => {
      const value = urlParams.get(keyMap[param.toLowerCase()]);

      if (value === "open")
        return "open";
      if (value === "close" || value === "closed")
        return "closed";
      if (value === "hide" || value === "hidden")
        return "hidden";

      return defaulState;
    };

    const getStringValue = (param: string) => {
      return urlParams.get(keyMap[param.toLowerCase()]) ?? undefined;
    };

    const getStringValues = (param: string) => {
      if (!urlParams.has(keyMap[param.toLowerCase()]))
        return undefined;

      return urlParams.getAll(keyMap[param.toLowerCase()]);
    };

    const getBooleanValue = (param: string) => {
      const value = urlParams.get(keyMap[param.toLowerCase()]);
      if (value === null)
        return undefined;

      return value.toLowerCase() === "true";
    };

    const getNumberValue = (param: string) => {
      const value = urlParams.get(keyMap[param.toLowerCase()]);
      if (value === null)
        return undefined;

      const number = parseInt(value, 10);
      return !isNaN(number) ? number : undefined;
    };

    const layout: LayoutState = {
      mainHeader: getBooleanValue("mainHeader"),
      toolbars: getBooleanValue("headers"),
      filePanel: resolve("filePane", "open"),
      filePanelSize: getNumberValue("filePaneSize"),
      editorPanel: resolve("editorPane", "open"),
      editorPanelSize: getNumberValue("editorPaneSize"),
      editorLineNumbers: getBooleanValue("lineNumbers"),
      editorMiniMap: getBooleanValue("minimap"),
      editorCodeFolding: getBooleanValue("codeFolding"),
      previewPanel: resolve("previewPane", "open"),
      problemsPanel: resolve("problemsPane"),
      overviewPanel: resolve("overviewPane"),
      walkthroughPanel: resolve("walkthroughPane"),
      packagesPanel: resolve("packagesPane"),
      statusPanels: resolve("statusPanes"),
      popoutButton: getBooleanValue("popoutButton"),
    }

    const settings: GlobalSettings = {
      filePath: getStringValues("file"),
      activeFilePath: getStringValue("activeFile"),
      startLineNumber: getNumberValue("start"),
      endLineNumber: getNumberValue("end"),
      autoSave: getBooleanValue("autosave"),
      focusMode: parseFocusModeString(getStringValue("focusMode")),
      sessionId: getStringValue("sessionId"),
      showActionToasts: getBooleanValue("showActionToasts") ?? true,
    }

    const theme = getStringValue("theme");
    if (theme === "light" || theme === "dark" || theme === "os")
      dispatch(themeChanged(theme));

    if (Object.values(layout).some((value) => value !== undefined))
      dispatch(layoutStateChanged(layout));

    if (Object.values(settings).some((value) => value !== undefined))
      dispatch(globalSettingsChanged(settings));

  }, [location.search]);
};
